import Alert from "@pathwright/ui/src/components/alert/Alert"
import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Card from "@pathwright/ui/src/components/card/Card"
import CardHeader from "@pathwright/ui/src/components/card/CardHeader"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import { useQuery } from "@pathwright/web/src/modules/utils/apollo"
import get from "lodash/get"
import { useEffect } from "react"
import styled from "styled-components"
import { useCurrencyFormatter } from "../currency/CurrencyFormatterProvider"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import LICENSE_OFFERING_QUERY from "../resource/license/graphql/license-offering-query"
import RESOURCE_LICENSE_QUERY from "../resource/license/graphql/resource-license-query"
import { getGroupUrl } from "../utils/urls"
import useOrderPollingQuery from "./useOrderPollingQuery"

const NarrowCard = styled(Card)`
  & > .View {
    max-width: 500px;
  }
`

const CenteredContent = styled.div`
  padding: 40px 25px;
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
`

export default function GroupLicenseOrderConfirmationCard({
  card,
  orderId,
  licenseId
}) {
  const { school } = usePathwrightContext()
  const { currencyFormatter } = useCurrencyFormatter()
  const orderQuery = useOrderPollingQuery(orderId)
  const { tc } = useTranslate()

  const licenseQuery = useQuery(LICENSE_OFFERING_QUERY, {
    variables: {
      id: licenseId,
      school_id: school.id
    }
  })
  const order = get(orderQuery, "data.order")
  const license = get(licenseQuery, "data.context.school.license_offering")
  const resource = get(license, "resource")

  const resourceLicenseQuery = useQuery(RESOURCE_LICENSE_QUERY, {
    skip: !order || !order.fulfilled_object_id,
    variables: {
      id: order && order.fulfilled_object_id
    }
  })
  const resourceLicense = get(resourceLicenseQuery, "data.resource_license")

  useEffect(() => {
    if (get(order, "order_status") === "fulfilled") {
      // HACK: bootstrapping the store will ensure the
      // window.bootstrappedData.permissions.user_is_teaching flag will be updated
      window.App && window.App.getStore("school").action.bootstrapSchool()
    }
  }, [get(order, "order_status")])

  return (
    <NarrowCard card={card} navless>
      {resource && license ? (
        <>
          <CardHeader
            card={card}
            resource={resource}
            title={resource.name}
            meta={
              license.cost_per_seat > 0
                ? `Create Your Own ${tc(
                    "cohort.label"
                  )} • ${currencyFormatter.customFormat(
                    license.cost_per_seat
                  )}/member`
                : `Create Your Own ${tc("cohort.label")}`
            }
          />
          {orderQuery.error ? (
            <CenteredContent>
              <Alert error={orderQuery.error} />
            </CenteredContent>
          ) : order && order.order_status === "fulfilled" ? (
            <BlankSlate
              animateIcon
              stack
              icons={null}
              icon="check"
              heading="Ready To Teach!"
              body={`Congratulations, you're ready to start taking ${resource.name} with your group. You'll receive an email confirmation and receipt shortly.`}
              primaryAction={
                resourceLicense
                  ? {
                      to: getGroupUrl(
                        resource.slug,
                        resourceLicense.group.id,
                        "path"
                      ),
                      children: "Get Started"
                    }
                  : null
              }
            />
          ) : (
            <BlankSlate
              icons={null}
              heading="Processing order..."
              primaryAction={{
                brand: false,
                submitting: true
              }}
            />
          )}
        </>
      ) : (
        <LoadingCircle />
      )}
    </NarrowCard>
  )
}

GroupLicenseOrderConfirmationCard.displayName =
  "GroupLicenseOrderConfirmationCard"
